const _apiBaseEnv = process.env.REACT_APP_API_BASE;
const _isProdBuild = process.env.NODE_ENV === "production";
const _looksLocalhost = (url) => /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i.test(url);

if (!_apiBaseEnv && _isProdBuild) {
  throw new Error("REACT_APP_API_BASE must be set in production builds");
}
// A production build silently talking to localhost is always a
// misconfiguration (a stray/missing env var at build time, e.g. from
// .env.railway), never something intentional — fail loudly at module load
// rather than let it ship a broken API/WS host.
if (_isProdBuild && _looksLocalhost(_apiBaseEnv || "")) {
  throw new Error(
    "REACT_APP_API_BASE resolves to a localhost URL in a production build " +
    `("${_apiBaseEnv}") — this must be the deployed backend host. Check the build environment's REACT_APP_API_BASE.`
  );
}
export const API_BASE = _apiBaseEnv || "http://localhost:8000";
export const WS_BASE  = API_BASE.replace(/^http/, "ws");

// FastAPI's `detail` is a string, a structured object ({message, ...}), or
// a 422 validation list. new Error(object) renders as "[object Object]",
// so turn every shape into readable text.
function errorMessage(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d && typeof d.msg === "string" ? d.msg : ""))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return "";
}

export async function handleResponse(response) {
  const text = await response.text();
  if (!response.ok) {
    let message = response.statusText;
    let detail;
    try {
      const json = JSON.parse(text);
      message = errorMessage(json.detail) || json.message || text;
      detail = json.detail;
    } catch {
      message = text || response.statusText;
    }
    const error = new Error(message);
    error.status = response.status;
    // The raw detail, for callers that can say more than the message
    // (e.g. the seller application's missing_fields list).
    error.detail = detail;
    throw error;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Client-side TTL cache for public product GET requests ──────
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function cachedFetch(url) {
  const hit = _cache.get(url);
  if (hit && Date.now() - hit.ts < CACHE_TTL) return Promise.resolve(hit.data);
  return fetch(url)
    .then(r => {
      if (!r.ok) { const error = new Error(r.statusText || "Request failed"); error.status = r.status; throw error; }
      return r.json();
    })
    .then(data => { _cache.set(url, { data, ts: Date.now() }); return data; });
}

export function bustProductCache() {
  for (const key of _cache.keys()) {
    if (key.includes("/products")) _cache.delete(key);
  }
}

export function bustCategoryCache() {
  for (const key of _cache.keys()) {
    if (key.includes("/categories")) _cache.delete(key);
  }
}
